import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { Modal } from '../../components/common/Modal';
import type { GreenBeanProfile } from '../../types/greenBean';
import { useGreenBeanProfileForm } from './useGreenBeanProfileForm';
import { GreenBeanItemInfoSection } from './GreenBeanItemInfoSection';
import { GreenBeanPhysicalSection } from './GreenBeanPhysicalSection';
import { GreenBeanExtrinsicSection } from './GreenBeanExtrinsicSection';
import { GreenBeanNotesSection } from './GreenBeanNotesSection';
import { useLanguage } from '../../features/language/useLanguage';
import { usePermissions } from '../../hooks/usePermissions';

interface GreenBeanProfileFormProps {
  initialData?: Partial<GreenBeanProfile>;
  onSuccess?: () => void;
  onCancel?: () => void;
  hideLotSection?: boolean;
  entityType?: 'item' | 'lot' | 'sample' | 'ItemType' | 'LotType' | 'SampleType';
  entityId?: string;
  readOnly?: boolean;
}

export const GreenBeanProfileForm: React.FC<GreenBeanProfileFormProps> = ({
  initialData,
  onSuccess,
  onCancel,
  hideLotSection,
  entityType,
  entityId,
  readOnly: readOnlyProp
}) => {
  const { t } = useLanguage();
  const { can } = usePermissions();
  const canWrite = can('inventory', 'write');
  const readOnly = readOnlyProp ?? !canWrite;
  const navigate = useNavigate();
  const [presetActionError, setPresetActionError] = useState<string | null>(null);
  const {
    control,
    register,
    handleSubmit,
    formState,
    isEditMode,
    isLoadingProfile,
    onSubmit,
    uniqueOptions,
    itemInfo,
    lotInfo,
    lotIdDisplay,
    isItemProfilePromptOpen,
    applyItemProfilePreset,
    skipItemProfilePreset,
    processType,
    enableFlags,
  } = useGreenBeanProfileForm({
    initialData,
    hideLotSection,
    entityType,
    entityId,
    onSuccess,
  });

  const { errors, isSubmitting } = formState;

  if (isEditMode && isLoadingProfile && !initialData) {
    return <LoadingSpinner text={t('greenBeanProfile.loading')} />;
  }

  const handleApplyPreset = () => {
    try {
      setPresetActionError(null);
      applyItemProfilePreset();
    } catch (error) {
      console.error('Failed to apply item profile preset', error);
      setPresetActionError(t('greenBeanProfile.errors.applyPresetFailed'));
    }
  };

  const handleSkipPreset = () => {
    try {
      setPresetActionError(null);
      skipItemProfilePreset();
    } catch (error) {
      console.error('Failed to skip item profile preset', error);
      setPresetActionError(t('greenBeanProfile.errors.skipPresetFailed'));
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-10">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8 bg-white p-6 rounded-lg shadow-sm">
        <fieldset disabled={readOnly} className={readOnly ? 'opacity-80 pointer-events-none' : undefined}>
          <GreenBeanItemInfoSection itemInfo={itemInfo} lotIdDisplay={lotIdDisplay} lotInfo={lotInfo} />
          <GreenBeanPhysicalSection control={control} register={register} errors={errors} />
          <GreenBeanExtrinsicSection
            control={control}
            register={register}
            uniqueOptions={uniqueOptions}
            enableFlags={enableFlags}
            processType={processType}
            errors={errors}
          />
          <GreenBeanNotesSection control={control} register={register} />

          <input type="hidden" {...register('lot_id')} />
          <input type="hidden" {...register('roast_id')} />
          <input type="hidden" {...register('qc_cupping')} />
          <input type="hidden" {...register('origin_details')} />
          <input type="hidden" {...register('taste_profile')} />

          <input type="hidden" {...register('inventory_item_id')} />
          <input type="hidden" {...register('type')} />
          <input type="hidden" {...register('sample_id')} />

        </fieldset>

        <div className="flex justify-end gap-4 pt-4 border-t">
          <button
            type="button"
            onClick={() => {
              if (onCancel) {
                onCancel();
              } else {
                navigate(-1);
              }
            }}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            data-test-id="green-bean-cancel-button"
          >
            {readOnly ? t('forms.actions.close') : t('auth.common.cancel')}
          </button>
          {!readOnly && (
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              data-test-id="green-bean-submit-button"
            >
              {isSubmitting ? t('forms.actions.saving') : (isEditMode ? t('greenBeanProfile.actions.update') : t('greenBeanProfile.actions.create'))}
            </button>
          )}
        </div>
      </form>
      <Modal
        isOpen={isItemProfilePromptOpen}
        onClose={handleSkipPreset}
        title={t('greenBeanProfile.useItemProfileTitle')}
        maxWidth="md"
        zIndexClass="z-[70]"
      >
        <p className="text-sm text-gray-600">
          {t('greenBeanProfile.useItemProfileMessage')}
        </p>
        {presetActionError && (
          <p className="mt-3 text-sm text-red-600">{presetActionError}</p>
        )}
        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleSkipPreset}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            {t('cupping.formSections.labels.no')}
          </button>
          <button
            type="button"
            onClick={handleApplyPreset}
            className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
          >
            {t('cupping.formSections.labels.yes')}
          </button>
        </div>
      </Modal>
    </div>
  );
};
